import { DropdownChevron } from '#/components/Dropdown/Chevron/DropdownChevron';
import { Dropdown } from '#/components/Dropdown/Dropdown';
import { DropdownFoldMenu } from '#/components/Dropdown/menus/Fold/DropdownFoldMenu';
import { useUser } from '#/contexts/UserEnvironmentContext';
import { useIsClient } from '#/hooks/useIsClient';
import { useViewportBreakpoint } from '#/hooks/viewport/useViewportBreakpoint';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import { LayoutHeaderButton } from '#/layouts/Layout/Header/Button/LayoutHeaderButton';
import { isAdmin } from '#/utils/userEnvironment';

import { participateDropdownDataByLocale } from './data/participateDropdownDataByLocale';
import {
  participateDropdownDataByResourcesItemType,
  participateDropdownDataBySearchItemType,
} from './ParticipateDropdownConstants';

const triggerTextByLocale: Record<
  I18nLocale.ES | I18nLocale.PT,
  { text: string; href: string }
> = {
  es: {
    text: 'Participa',
    href: '/es/dias/participa',
  },
  pt: {
    text: 'Participar',
    href: '/pt/movimento',
  },
};

type Props = {
  locale: I18nLocale.ES | I18nLocale.PT;
};

export function ParticipateDropdown({ locale }: Props) {
  const { user } = useUser();
  const isAdminUser = isAdmin(user);
  const { search, resources } = participateDropdownDataByLocale[locale];

  const isClient = useIsClient();
  const viewport = useViewportBreakpoint();
  const isDesktop = isClient && ['md', 'lg'].includes(viewport);

  if (locale !== I18nLocale.ES && locale !== I18nLocale.PT) {
    return;
  }

  const { text, href } = triggerTextByLocale[locale];

  return (
    <Dropdown
      trigger={
        <LayoutHeaderButton
          data={
            isDesktop
              ? {
                  type: 'link',
                  href,
                  trackClicked: {
                    name: 'Participate',
                    properties: { context: 'Header' },
                  },
                }
              : { type: 'inert' }
          }
          variant="grey"
          qaId="page-header-participate-dropdown-trigger"
        >
          {text} <DropdownChevron />
        </LayoutHeaderButton>
      }
      desktopTriggersOnHover
    >
      <DropdownFoldMenu
        left={{
          title: search.title,
          items: search.items.map((searchItem) => {
            const { iconName, qaId, trackClickedName } =
              participateDropdownDataBySearchItemType[searchItem.type];

            return {
              iconName,
              title: searchItem.title,
              href:
                isAdminUser && searchItem.adminUserHref
                  ? searchItem.adminUserHref
                  : searchItem.href,
              qaId,
              trackClicked: {
                name: trackClickedName,
                context: 'Header Dropdown > Participate',
              },
            };
          }),
        }}
        right={{
          title: resources.title,
          items: resources.items.map((resourceItem) => {
            const { image, imageHover, qaId, trackClickedName } =
              participateDropdownDataByResourcesItemType[resourceItem.type];

            return {
              image,
              imageHover,
              title: resourceItem.title,
              description: resourceItem.description,
              href: resourceItem.href,
              qaId,
              trackClicked: {
                name: trackClickedName,
                context: 'Header Dropdown > Participate',
              },
            };
          }),
        }}
      />
    </Dropdown>
  );
}
